export const PORTFOLIO_V2_SCHEMA_VERSION = "tradvera.portfolio-v2.v1" as const;

export type PortfolioV2SchemaVersion = typeof PORTFOLIO_V2_SCHEMA_VERSION;
export type DecimalValue = string;
export type IsoDateTime = string;
export type CurrencyCode = string;
export type PortfolioLocaleV2 = "ar" | "en";

export interface BilingualTextV2 {
  ar: string;
  en: string;
}

export const PORTFOLIO_BOOK_KINDS_V2 = ["PAPER", "OWNED_PRIVATE"] as const;
export type PortfolioBookKindV2 = typeof PORTFOLIO_BOOK_KINDS_V2[number];
export const PORTFOLIO_BOOK_STATUSES_V2 = ["DRAFT", "ACTIVE", "FROZEN", "ARCHIVED"] as const;
export type PortfolioBookStatus = typeof PORTFOLIO_BOOK_STATUSES_V2[number];
export const PORTFOLIO_BOOK_INITIALIZATION_STATES_V2 = [
  "LEGACY", "OPENING_CASH_RECORDED", "AWAITING_FIRST_STATEMENT",
  "FIRST_SOURCE_BOUND_STAGING", "STATEMENT_ORIGIN_RECORDED",
] as const;
export type PortfolioBookInitializationStateV2 =
  typeof PORTFOLIO_BOOK_INITIALIZATION_STATES_V2[number];
export const PORTFOLIO_ASSET_CLASSES_V2 = ["US_STOCK", "US_ETF", "FX", "CRYPTO", "OTHER"] as const;
export type PortfolioAssetClassV2 = typeof PORTFOLIO_ASSET_CLASSES_V2[number];
export const PORTFOLIO_SUPPORT_STATES_V2 = ["SUPPORTED", "RECORD_ONLY"] as const;
export type PortfolioSupportStateV2 = typeof PORTFOLIO_SUPPORT_STATES_V2[number];
/** @deprecated Use PortfolioSupportStateV2. */
export type PortfolioSupportState = PortfolioSupportStateV2;
export const PORTFOLIO_CAPITAL_NATURES_V2 = ["VIRTUAL", "OWNER_FUNDS"] as const;
export const PORTFOLIO_SOURCE_MODES_V2 = ["MANUAL", "STATEMENT_IMPORT", "SIMULATOR_IMPORT"] as const;
export const SOURCE_FRESHNESS_STATES_V2 = ["FRESH", "STALE", "PARTIAL", "UNKNOWN"] as const;
export const EVIDENCE_KINDS_V2 = ["FACT", "RULE", "RESEARCH", "MARKET", "OWNER_NOTE", "EXTERNAL_ANALYST"] as const;

export interface PortfolioScopeV2 {
  projectId: string;
  ownerId: string;
  workspaceId: string;
  bookId: string;
}

export interface SourceStampV2 {
  sourceId: string;
  sourceRecordId?: string;
  asOf: IsoDateTime;
  knownAt: IsoDateTime;
  freshness: typeof SOURCE_FRESHNESS_STATES_V2[number];
}

export interface EvidenceReferenceV2 {
  evidenceId: string;
  kind: typeof EVIDENCE_KINDS_V2[number];
  knownAt: IsoDateTime;
  sourceId?: string;
}

export const PORTFOLIO_V2_ERROR_CODES = [
  "BOOK_MISMATCH",
  "BOOK_NOT_ACTIVE",
  "BOOK_VERSION_CONFLICT",
  "COMMAND_INVALID",
  "POLICY_REQUIRED",
  "POLICY_INVALID",
  "POLICY_VERSION_MISMATCH",
  "INVALID_DECIMAL",
  "INVALID_CURRENCY",
  "FEE_DETAILS_INVALID",
  "INVALID_RISK_GEOMETRY",
  "REWARD_TO_RISK_BELOW_POLICY",
  "RISK_LIMIT_EXHAUSTED",
  "INSUFFICIENT_CASH",
  "PLAN_NOT_FOUND",
  "PLAN_NOT_EXECUTABLE",
  "LOT_NOT_FOUND",
  "LOT_NOT_OPEN",
  "QUANTITY_EXCEEDS_REMAINING",
  "EXIT_REASON_REQUIRED",
  "EXIT_REASON_NOT_LOCKED",
  "RESEARCH_CONTEXT_MISMATCH",
  "IDEMPOTENCY_CONFLICT",
  "RESOURCE_CONFLICT",
  "UNSUPPORTED_ASSET",
  "UNBALANCED_LEDGER",
  "MODEL_ROUTE_NOT_APPROVED",
  "EXECUTION_NOT_FOUND",
  "CORRECTION_NOT_ALLOWED",
  "CORPORATE_ACTION_NOT_APPLICABLE",
] as const;

export type PortfolioV2ErrorCode = typeof PORTFOLIO_V2_ERROR_CODES[number];

export interface PortfolioV2Failure {
  ok: false;
  code: PortfolioV2ErrorCode;
  messageCode: PortfolioV2ErrorCode;
  detail?: Record<string, unknown>;
}

export interface PortfolioV2Success<T> {
  ok: true;
  value: T;
  replayed: boolean;
}

export type PortfolioV2Result<T> = PortfolioV2Success<T> | PortfolioV2Failure;
